/**
 * V15 MIDI 文件解析器 — 零依赖，纯 TypeScript
 *
 * 解析 Standard MIDI File (SMF) 格式 0/1。
 * 提取 Note On/Off、Program Change、Tempo 事件。
 */

// ---- Types ----

export interface MidiNote {
  pitch: number;      // 0-127 MIDI note number
  velocity: number;   // 0-127
  startTime: number;  // seconds from start
  duration: number;   // seconds
  channel: number;    // 0-15
}

export interface MidiTrack {
  notes: MidiNote[];
  program: number;    // 0-127, default 0 (Acoustic Grand Piano)
}

export interface MidiFile {
  tracks: MidiTrack[];
  tempo: number;        // BPM
  duration: number;     // total seconds
  division: number;     // ticks per quarter note (or negative for SMPTE)
  format: number;       // 0 or 1
}

// ---- Binary Reader ----

class BinaryReader {
  private view: DataView;
  pos: number = 0;

  constructor(buffer: ArrayBuffer) {
    this.view = new DataView(buffer);
  }

  eof(): boolean { return this.pos >= this.view.byteLength; }
  skip(n: number): void { this.pos += n; }

  readUint8(): number {
    return this.view.getUint8(this.pos++);
  }

  readUint16(): number {
    const v = this.view.getUint16(this.pos);
    this.pos += 2;
    return v;
  }

  readUint32(): number {
    const v = this.view.getUint32(this.pos);
    this.pos += 4;
    return v;
  }

  readString(length: number): string {
    let s = '';
    for (let i = 0; i < length; i++) {
      s += String.fromCharCode(this.readUint8());
    }
    return s;
  }

  readVLQ(): number {
    let value = 0;
    for (let i = 0; i < 4; i++) {
      const byte = this.readUint8();
      value = (value << 7) | (byte & 0x7f);
      if ((byte & 0x80) === 0) break;
    }
    return value;
  }
}

// ---- Main Parser ----

export function parseMidi(buffer: ArrayBuffer): MidiFile {
  const reader = new BinaryReader(buffer);

  // Header chunk
  const headerId = reader.readString(4);
  if (headerId !== 'MThd') {
    throw new Error(`[midi] invalid MIDI header: ${headerId}`);
  }
  const headerSize = reader.readUint32();
  if (headerSize < 6) {
    throw new Error(`[midi] header too short: ${headerSize}`);
  }

  const format = reader.readUint16();
  const ntrks = reader.readUint16();
  const division = reader.readUint16();

  if (format > 1) {
    throw new Error(`[midi] unsupported format: ${format} (only 0/1 supported)`);
  }

  // Parse tracks
  const allTracks: MidiTrack[] = [];
  const allTempos: Array<{ time: number; bpm: number }> = [];
  let maxTime = 0;

  for (let t = 0; t < ntrks; t++) {
    const trackId = reader.readString(4);
    if (trackId !== 'MTrk') {
      throw new Error(`[midi] invalid track header: ${trackId}`);
    }
    const trackSize = reader.readUint32();
    const trackEnd = reader.pos + trackSize;

    const track = parseTrack(reader, trackEnd, division, allTempos);
    allTracks.push(track);
    
    for (const note of track.notes) {
      const endTime = note.startTime + note.duration;
      if (endTime > maxTime) maxTime = endTime;
    }
  }

  // Determine tempo: use first tempo event, or default 120 BPM
  const firstTempo = allTempos.length > 0 ? allTempos[0] : { time: 0, bpm: 120 };
  const tempo = firstTempo.bpm;

  // Convert note times from ticks to seconds using the first tempo
  const ticksPerBeat = division;
  const secondsPerTick = 60 / (tempo * ticksPerBeat);

  // Note: note times are already in tick units; conversion happens in midi-synth

  return {
    tracks: allTracks.filter(t => t.notes.length > 0),
    tempo,
    duration: maxTime * secondsPerTick,
    division,
    format,
  };
}

// ---- Track Parser ----

interface PendingNote {
  pitch: number;
  velocity: number;
  channel: number;
  startTick: number;
}

function parseTrack(
  reader: BinaryReader,
  trackEnd: number,
  _division: number,
  tempoEvents: Array<{ time: number; bpm: number }>
): MidiTrack {
  const notes: MidiNote[] = [];
  const pendingNotes = new Map<string, PendingNote>();
  let program = 0;
  let runningStatus: number | null = null;
  let absoluteTick = 0;

  while (reader.pos < trackEnd && !reader.eof()) {
    const delta = reader.readVLQ();
    absoluteTick += delta;

    let status = reader.readUint8();

    // Running status
    if ((status & 0x80) === 0) {
      if (runningStatus === null) {
        // Corrupt? skip
        continue;
      }
      reader.pos--; // rewind, this byte is data
      status = runningStatus;
    } else {
      runningStatus = status;
    }

    const eventType = status & 0xf0;
    const channel = status & 0x0f;

    if (eventType === 0x80) {
      // Note Off
      const pitch = reader.readUint8();
      reader.readUint8(); // velocity (unused for note-off)
      const key = `${channel}:${pitch}`;
      const pending = pendingNotes.get(key);
      if (pending) {
        notes.push({
          pitch: pending.pitch,
          velocity: pending.velocity,
          startTime: pending.startTick,
          duration: absoluteTick - pending.startTick,
          channel: pending.channel,
        });
        pendingNotes.delete(key);
      }
    } else if (eventType === 0x90) {
      // Note On
      const pitch = reader.readUint8();
      const velocity = reader.readUint8();
      const key = `${channel}:${pitch}`;
      
      if (velocity === 0) {
        // Velocity 0 = Note Off
        const pending = pendingNotes.get(key);
        if (pending) {
          notes.push({
            pitch: pending.pitch,
            velocity: pending.velocity,
            startTime: pending.startTick,
            duration: absoluteTick - pending.startTick,
            channel: pending.channel,
          });
          pendingNotes.delete(key);
        }
      } else {
        // If there's already a pending note with this key, close it first
        const existing = pendingNotes.get(key);
        if (existing) {
          notes.push({
            pitch: existing.pitch,
            velocity: existing.velocity,
            startTime: existing.startTick,
            duration: absoluteTick - existing.startTick,
            channel: existing.channel,
          });
        }
        pendingNotes.set(key, {
          pitch,
          velocity,
          channel,
          startTick: absoluteTick,
        });
      }
    } else if (eventType === 0xc0) {
      // Program Change
      program = reader.readUint8();
    } else if (eventType === 0xb0) {
      // Control Change — skip 2 bytes
      reader.skip(2);
    } else if (eventType === 0xe0) {
      // Pitch Bend — skip 2 bytes
      reader.skip(2);
    } else if (status === 0xff) {
      // Meta event
      const metaType = reader.readUint8();
      const metaLength = reader.readVLQ();

      if (metaType === 0x51) {
        // Tempo
        if (metaLength === 3) {
          const b1 = reader.readUint8();
          const b2 = reader.readUint8();
          const b3 = reader.readUint8();
          const microsPerQuarter = (b1 << 16) | (b2 << 8) | b3;
          const bpm = Math.round(60000000 / microsPerQuarter);
          tempoEvents.push({ time: absoluteTick, bpm });
        }
      } else if (metaType === 0x2f) {
        // End of Track
        break;
      } else {
        reader.skip(metaLength);
      }
    } else if (eventType === 0xf0) {
      // System Exclusive
      const len = reader.readVLQ();
      reader.skip(len);
    } else {
      // Unknown — try to skip
      console.warn(`[midi] unknown event type: 0x${eventType.toString(16)} at tick ${absoluteTick}`);
      reader.skip(1);
    }
  }

  return { notes, program };
}
